import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Tabs, ChecklistsContainer } from 'components/ideal-core-ui'
import { URLS } from 'utils/constants'
import { DealDetailsContainer } from './DealDetailsContainer'
import { ProposalsContainer } from './ProposalsContainer'
import { Docs } from './Docs'
import { ActivitySummary } from './ActivitySummary'
import { DealNotes } from './DealNotes'
import { Container, Row, Col, Badge, UncontrolledTooltip } from 'reactstrap'
import { DealSidebar } from './DealSidebar'
import { DealTasksContainer } from './DealTasksContainer'
import s from './DealContainer.scss'
import { useSelector, useDispatch } from 'react-redux'
import * as ACT from './actions'
import { makeGetRequest } from 'utils/api'
import { PageTitle } from 'components/common/PageTitle'
import { timezoneDate } from 'utils/helpers'
import { FaCircle, FaInfoCircle } from 'react-icons/fa'
import pluralize from 'pluralize'
import { DealAnalysisContainer } from './DealAnalysisContainer'
import { DealTimeTracker } from './DealTimeTracker'
import { DealEstimatingCosts } from 'components/common/DealEstimatingCosts'
import { isTimetrackerAdmin, isTimetrackerRegular } from 'utils/authService'

const DealContainer = () => {
  const location = useLocation()
  const activeTab = location.pathname.split('/')[3]
  const { id } = useParams()
  const dispatch = useDispatch()
  const {
    dealCompanies,
    tasks,
    awardedCompany,
    status,
    statusCategory,
    dealManager,
    shouldUpdateSidebar
  } = useSelector(state => state.deal)
  const isSidebarOpen = useSelector(state => state.sidebar.dealSidebar.isOpen)
  const [localAwardedCompany, setLocalAwardedCompany] = useState(null)

  const dealSummary = useSelector(state => state.deal.summaryInfo)
  const notes = useSelector(state => state.deal.notes)
  const documentsCount = useSelector(state => state.deal.documentsCount)

  useEffect(() => {
    dispatch({
      type: ACT.DEAL_SUMMARY_INFO_FETCH_REQUESTED,
      payload: {
        id
      }
    })

    dispatch({
      type: ACT.DEAL_NOTES_FETCH_REQUESTED,
      payload: {
        id
      }
    })

    dispatch({
      type: ACT.FETCH_DEAL_DOCUMENTS_COUNT_REQUESTED,
      payload: {
        dealId: id
      }
    })

    if (!activeTab || activeTab === 'details') return

    dispatch({
      type: ACT.DEAL_COMPANIES_FETCH_REQUESTED,
      payload: {
        dealId: id
      }
    })
    ;(async () => {
      const company = await makeGetRequest(`/v1/deals/awarded-company/${id}`)
      setLocalAwardedCompany(company ? { value: company.id, label: company.name } : null)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      dispatch({
        type: ACT.CLEAR_DEAL_FROM_STORE
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setLocalAwardedCompany(awardedCompany)
  }, [awardedCompany])

  return (
    <>
      <PageTitle detail={dealSummary.name} />
      <h4 className={s.name}>
        {dealSummary.name}
        {dealSummary.quoteNumber && ` - ${dealSummary.quoteNumber}`}
        {dealSummary.projectNumber && ` - ${dealSummary.projectNumber}`}
        {dealSummary.breadCrumb && (
          <p style={{ marginBottom: 0, fontSize: 14, color: '#007bff' }}>
            {dealSummary.breadCrumb}
            <FaInfoCircle id="Type1" style={{ marginLeft: 5 }} />
            <UncontrolledTooltip placement="top" target="Type1">
              This is the email address of your opportunity. Emails sent to this email address will
              be saved under the opportunity notes
            </UncontrolledTooltip>
          </p>
        )}
      </h4>
      <Col lg="9">
        <p className={s.briefNote}>
          Age: {pluralize('day', dealSummary.opportunityAge, true)}
          <FaCircle className={s.circle} />
          Opportunity Last Updated on: {timezoneDate(dealSummary.lastUpdated)}
        </p>
      </Col>
      <Col lg="9">
        <p className={s.briefNote}>{dealSummary.briefNote}</p>
      </Col>
      <Container className={s.container}>
        <Row>
          <Col lg={isSidebarOpen ? '9' : '11'} className={s.main}>
            <div className={s.dealStatusBarContainer}>
              <div
                className={s.dealStatusBar}
                style={{
                  backgroundColor: dealSummary.status?.color || dealSummary.status?.category.color
                }}
              />
            </div>
            <Tabs
              withRoutes
              className={s.tabs}
              activeTabKey={activeTab || 'details'}
              tabs={[
                {
                  index: true,
                  key: 'details',
                  label: 'Details',
                  path: URLS.AUTH_DEAL_DETAILS,
                  element: <DealDetailsContainer />
                },
                {
                  label: 'Proposals',
                  key: 'proposals',
                  path: URLS.AUTH_DEAL_PROPOSALS,
                  element: (
                    <ProposalsContainer
                      dealCompanies={dealCompanies}
                      awardedCompany={localAwardedCompany}
                    />
                  )
                },
                {
                  label: (
                    <>
                      Docs{' '}
                      <Badge className={s.tasksBadge} color="secondary" pill>
                        {Object.keys(documentsCount).reduce(
                          (acc, key) => acc + documentsCount[key],
                          0
                        )}
                      </Badge>
                    </>
                  ),
                  key: 'documents',
                  path: URLS.AUTH_DEAL_DOCUMENTS,
                  element: (
                    <Docs
                      documentsCount={documentsCount}
                      dealCompanies={dealCompanies}
                      awardedCompany={localAwardedCompany}
                    />
                  )
                },
                {
                  label: (
                    <>
                      Tasks{' '}
                      <Badge className={s.tasksBadge} color="secondary" pill>
                        {tasks?.length
                          ? tasks.filter(t => !t.dateCompleted).length
                          : dealSummary.incompleteTaskCount}
                      </Badge>
                    </>
                  ),
                  key: 'tasks',
                  path: URLS.AUTH_DEAL_TASKS,
                  element: <DealTasksContainer />
                },
                {
                  label: 'Checklists',
                  key: 'checklists',
                  path: URLS.AUTH_DEAL_CHECKLISTS,
                  element: <ChecklistsContainer entity="deal" />
                },
                {
                  label: 'Estimating',
                  key: 'estimating',
                  path: URLS.AUTH_DEAL_ESTIMATING,
                  element: <DealEstimatingCosts entity="deal" />
                },
                {
                  label: 'Analysis',
                  key: 'analysis',
                  path: URLS.AUTH_DEAL_ANALYSIS,
                  element: <DealAnalysisContainer />
                },
                {
                  label: 'Time Tracker',
                  key: 'timetracker',
                  path: URLS.AUTH_DEAL_TIMETRACKER,
                  element: <DealTimeTracker />,
                  disabled: !isTimetrackerAdmin() && !isTimetrackerRegular()
                },
                {
                  label: 'Activity',
                  key: 'activity',
                  path: URLS.AUTH_DEAL_ACTIVITY,
                  element: <ActivitySummary />
                },
                {
                  label: (
                    <>
                      Notes{' '}
                      <Badge className={s.tasksBadge} color="secondary" pill>
                        {notes?.length}
                      </Badge>
                    </>
                  ),
                  key: 'notes',
                  path: URLS.AUTH_DEAL_NOTES,
                  element: <DealNotes notes={notes} />
                }
              ]}
            />
          </Col>
          {isSidebarOpen ? (
            <Col lg="3" className={s.sidebar}>
              <DealSidebar
                dealCompanies={dealCompanies}
                awardedCompany={localAwardedCompany}
                dealStatusCode={status.value}
                dealManagerId={dealManager?.value}
                dealCategoryCode={statusCategory?.code}
                key={shouldUpdateSidebar}
              />
            </Col>
          ) : (
            <div className={s.sidebarCollapsed}>
              <DealSidebar
                dealCompanies={dealCompanies}
                awardedCompany={localAwardedCompany}
                dealStatusCode={status.value}
                dealManagerId={dealManager?.value}
                dealCategoryCode={statusCategory?.code}
                key={shouldUpdateSidebar}
              />
            </div>
          )}
        </Row>
      </Container>
    </>
  )
}

export default DealContainer
